import { inject, provideAppInitializer, type EnvironmentProviders } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { type Span, SpanStatusCode } from '@opentelemetry/api';

import { getTracer, isTelemetryEnabled, recordEvent } from './telemetry-api';

/**
 * Instrumente la navigation du router Angular : un span `router.navigate` par
 * navigation (→ Tempo) + un évènement `navigation` (→ Loki). Les navigations
 * étant séquentielles, on suit un seul span actif à la fois.
 */
export function provideRouterTelemetry(): EnvironmentProviders {
  return provideAppInitializer(() => {
    if (!isTelemetryEnabled()) return;

    const router = inject(Router);
    let activeSpan: Span | null = null;

    // Souscription à VIE APPLICATIVE : posée une seule fois dans un app initializer
    // (exécuté au bootstrap, jamais détruit avant l'arrêt de l'app). Pas de
    // unsubscribe / DestroyRef volontairement — son cycle de vie est celui de
    // l'application elle-même ; elle s'éteint avec elle.
    router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        activeSpan = getTracer().startSpan('router.navigate', {
          attributes: { 'navigation.url': event.url, 'navigation.trigger': event.navigationTrigger ?? '' },
        });
      } else if (event instanceof NavigationEnd) {
        recordEvent('navigation', { 'navigation.url': event.urlAfterRedirects });
        activeSpan?.setStatus({ code: SpanStatusCode.OK });
        activeSpan?.end();
        activeSpan = null;
      } else if (event instanceof NavigationCancel) {
        activeSpan?.setAttribute('navigation.cancel_reason', event.reason ?? '');
        activeSpan?.end();
        activeSpan = null;
      } else if (event instanceof NavigationError) {
        activeSpan?.setStatus({ code: SpanStatusCode.ERROR, message: String(event.error) });
        activeSpan?.end();
        activeSpan = null;
      }
    });
  });
}
